import React, { useState } from "react";
import { MotifAvatar, MotifButton, MotifTooltip } from "@ey-xd/motif-react";
import CommentInput from "./CommentInput";
import { useI18n } from "../../hooks/useI18n";

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffDay > 30) {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  if (diffDay > 0) return `${diffDay}d ago`;
  if (diffHr > 0) return `${diffHr}h ago`;
  if (diffMin > 0) return `${diffMin}m ago`;
  return "Just now";
}

const MAX_NESTING_DEPTH = 3;

const CommentThread = ({
  comment,
  depth = 0,
  currentUserEmail,
  onReply,
  onToggleReaction,
  readOnly = false,
}) => {
  const { t } = useI18n();
  const [showReplyInput, setShowReplyInput] = useState(false);

  const id = comment.commentId || comment.replyId;
  const replies = comment.replies || [];
  const reactions = comment.reactionDetails || [];

  const likeCount = reactions.filter((r) => r.reaction === "like").length;
  const dislikeCount = reactions.filter((r) => r.reaction === "dislike").length;

  const currentUserReaction = reactions.find(
    (r) =>
      r.userMailId?.toLowerCase() === currentUserEmail?.toLowerCase(),
  );
  const hasLiked = currentUserReaction?.reaction === "like";
  const hasDisliked = currentUserReaction?.reaction === "dislike";

  const handleReplySubmit = (message) => {
    onReply(id, message);
    setShowReplyInput(false);
  };

  const effectiveDepth = Math.min(depth, MAX_NESTING_DEPTH);

  return (
    <div
      className={`comment-thread ${effectiveDepth > 0 ? "comment-thread--nested" : ""}`}
    >
      <div className="comment-node">
        <div className="comment-avatar">
          <MotifAvatar size="small">
            {getInitials(comment.authorName)}
          </MotifAvatar>
        </div>

        <div className="comment-body">
          <div className="comment-header">
            <span className="comment-author motif-body2-default-medium">
              {comment.authorName}
            </span>
            <MotifTooltip
              trigger={
                <span className="comment-time motif-body2-default-regular">
                  {timeAgo(comment.createdAt)}
                </span>
              }
            >
              {new Date(comment.createdAt).toLocaleString()}
            </MotifTooltip>
          </div>

          <p className="comment-message motif-body1-default-regular">
            {comment.message}
          </p>

          <div className="comment-actions">
            <button
              type="button"
              className={`reaction-btn ${hasLiked ? "reaction-btn--active" : ""} ${readOnly ? "reaction-btn--readonly" : ""}`}
              onClick={readOnly ? undefined : () => onToggleReaction(id, "like")}
              disabled={readOnly}
              aria-label={t("comments_like", { defaultValue: "Like" })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
              </svg>
              {likeCount > 0 && (
                <span className="reaction-count">{likeCount}</span>
              )}
            </button>

            <button
              type="button"
              className={`reaction-btn ${hasDisliked ? "reaction-btn--active" : ""} ${readOnly ? "reaction-btn--readonly" : ""}`}
              onClick={readOnly ? undefined : () => onToggleReaction(id, "dislike")}
              disabled={readOnly}
              aria-label={t("comments_dislike", { defaultValue: "Dislike" })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10zM17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
              </svg>
              {dislikeCount > 0 && (
                <span className="reaction-count">{dislikeCount}</span>
              )}
            </button>

            {!readOnly && (
              <button
                type="button"
                className="reply-btn"
                onClick={() => setShowReplyInput(!showReplyInput)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 17 4 12 9 7" />
                  <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                </svg>
                {t("comments_reply", { defaultValue: "Reply" })}
              </button>
            )}
          </div>

          {!readOnly && showReplyInput && (
            <CommentInput
              onSubmit={handleReplySubmit}
              placeholder={t("comments_replyPlaceholder", {
                defaultValue: `Reply to ${comment.authorName}...`,
              })}
              submitLabel={t("comments_reply", { defaultValue: "Reply" })}
              autoFocus
              showCancel
              onCancel={() => setShowReplyInput(false)}
            />
          )}

          {replies.length > 0 && (
            <div className="comment-replies">
              {replies.map((reply) => (
                <CommentThread
                  key={reply.replyId || reply.commentId}
                  comment={reply}
                  depth={depth + 1}
                  currentUserEmail={currentUserEmail}
                  onReply={onReply}
                  onToggleReaction={onToggleReaction}
                  readOnly={readOnly}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentThread;
